import React, { useState, useRef, useEffect } from 'react';
import { documentAPI } from '../utils/api';
import './DocumentSelector.css';

export default function DocumentSelector({ 
  selectedDocuments = [], 
  onDocumentSelectionChange,
  className = "" 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [highlight, setHighlight] = useState(false);

  // Fetch documents when dropdown opens
  const fetchDocuments = async () => {
    if (availableDocuments.length > 0) return; // Don't fetch if we already have documents
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log('📋 Fetching documents from API...');
      const documents = await documentAPI.getDocuments();
      console.log('✅ Documents fetched:', documents.length, documents);
      setAvailableDocuments(documents);
    } catch (err) {
      console.error('❌ Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch documents when dropdown opens for the first time
  useEffect(() => {
    if (isOpen && availableDocuments.length === 0) {
      fetchDocuments();
    }
  }, [isOpen, availableDocuments.length]);

  // Listen for document upload events to refresh the list
  useEffect(() => {
    const handleDocumentUpload = () => {
      console.log('🔄 Document upload detected, refreshing document list...');
      setAvailableDocuments([]); // Clear current documents to force refetch
      if (isOpen) {
        fetchDocuments();
      }
    };

    // Listen for document upload/change events
    window.addEventListener('documentsUpdated', handleDocumentUpload);
    
    return () => {
      window.removeEventListener('documentsUpdated', handleDocumentUpload);
    };
  }, [isOpen]);

  // Filter documents based on search term
  const filteredDocuments = availableDocuments.filter(doc =>
    doc.originalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.fileName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHoveredIndex(-1);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Listen for external highlight requests
  useEffect(() => {
    const handleHighlight = () => {
      setHighlight(true);
      setTimeout(() => setHighlight(false), 1600);
    };
    window.addEventListener('documentSelector:highlight', handleHighlight);
    return () => window.removeEventListener('documentSelector:highlight', handleHighlight);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHoveredIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHoveredIndex(prev => 
          prev < filteredDocuments.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHoveredIndex(prev => 
          prev > 0 ? prev - 1 : filteredDocuments.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (hoveredIndex >= 0 && hoveredIndex < filteredDocuments.length) {
          toggleDocument(filteredDocuments[hoveredIndex]);
        }
        break;
      default:
        break;
    }
  };

  const toggleDocument = (document) => {
    const isSelected = selectedDocuments.some(doc => doc.id === document.id);
    
    if (isSelected) {
      // If clicking the already selected document, deselect it
      onDocumentSelectionChange([]);
    } else {
      // Single selection - replace current selection with this document
      onDocumentSelectionChange([document]);
    }
  };

  const clearSelection = () => {
    onDocumentSelectionChange([]);
  };

  const getDocumentName = (doc) => {
    return doc.originalName || doc.name || doc.fileName || 'Unnamed Document';
  };

  const getFileIcon = (doc) => {
    const name = getDocumentName(doc).toLowerCase();
    
    if (name.includes('.pdf')) {
      return (
        <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      );
    }
    
    if (name.includes('.doc') || name.includes('.docx')) {
      return (
        <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      );
    }
    
    if (name.includes('.txt')) {
      return (
        <svg className="w-6 h-6 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      );
    }
    
    if (name.includes('.xls') || name.includes('.xlsx')) {
      return (
        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      );
    }
    
    if (name.includes('.ppt') || name.includes('.pptx')) {
      return (
        <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      );
    }
    
    // Default document icon
    return (
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
      </svg>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`document-selector ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        className={`document-selector-trigger ${highlight ? 'needs-selection' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select documents for chat"
      >
        <div className="trigger-content">
          <div className="trigger-icon">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="trigger-text">
            {selectedDocuments.length === 0 ? (
              <span className="no-selection">Select Document</span>
            ) : (
              <span className="single-selection">
                {getDocumentName(selectedDocuments[0])}
              </span>
            )}
          </div>
          <div className="trigger-badge">
            {selectedDocuments.length > 0 && (
              <span className="selection-count">{selectedDocuments.length}</span>
            )}
          </div>
          <svg className={`trigger-arrow ${isOpen ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd"/>
          </svg>
        </div>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="document-dropdown-panel">
          {/* Header */}
          <div className="dropdown-header">
            <h3 className="dropdown-title">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M19 11H5m14-4H9m4 8H5m6-8V3m-2 16l2-2 2 2" />
              </svg>
              Document Library
            </h3>
            <div className="header-actions">
              <button 
                type="button"
                className="action-btn upload-btn"
                onClick={() => {
                  // Trigger file upload modal
                  if (window.openFilesModal) {
                    window.openFilesModal();
                  }
                }}
                title="Upload Documents"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </button>
              {selectedDocuments.length > 0 && (
                <button 
                  type="button"
                  className="action-btn clear-all"
                  onClick={clearSelection}
                  title="Clear Selection"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                      d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="search-container">
            <div className="search-input-wrapper">
              <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                onKeyDown={handleKeyDown}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Document List */}
          <div className="document-list">
            {isLoading ? (
              <div className="empty-state">
                <div className="loading-spinner">
                  <svg className="animate-spin w-8 h-8 text-indigo-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p>Loading documents...</p>
              </div>
            ) : error ? (
              <div className="empty-state">
                <svg className="empty-icon text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-red-600">Error: {error}</p>
                <button 
                  type="button" 
                  className="clear-search-btn"
                  onClick={() => fetchDocuments()}
                >
                  Try again
                </button>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="empty-state">
                {searchTerm ? (
                  <>
                    <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                        d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No documents found matching "{searchTerm}"</p>
                    <button 
                      type="button" 
                      className="clear-search-btn"
                      onClick={() => setSearchTerm('')}
                    >
                      Clear search
                    </button>
                  </>
                ) : availableDocuments.length === 0 ? (
                  <>
                    <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <h4 style={{ margin: '0.5rem 0', fontWeight: '600', color: '#374151' }}>No Documents Yet</h4>
                    <p>Upload your first document to start chatting</p>
                    <button 
                      type="button" 
                      className="upload-docs-btn"
                      onClick={() => {
                        if (window.openFilesModal) {
                          window.openFilesModal();
                        }
                        setIsOpen(false);
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Documents
                    </button>
                  </>
                ) : (
                  <>
                    <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707L16.586 6.586A1 1 0 0016 6.293V6a2 2 0 00-2-2H7a2 2 0 00-2 2v13a2 2 0 002 2z" />
                    </svg>
                    <p>No documents available</p>
                    <p className="empty-subtitle">Upload documents to start chatting</p>
                  </>
                )}
              </div>
            ) : (
              filteredDocuments.map((doc, index) => {
                const isSelected = selectedDocuments.some(d => d.id === doc.id);
                const isHovered = index === hoveredIndex;
                
                return (
                  <button
                    key={doc.id}
                    type="button"
                    className={`document-item ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                    onClick={() => toggleDocument(doc)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(-1)}
                  >
                    <div className="document-icon">
                      {getFileIcon(doc)}
                    </div>
                    
                    <div className="document-info">
                      <div className="document-name">
                        {getDocumentName(doc)}
                      </div>
                      <div className="document-meta">
                        {doc.size && (
                          <span className="file-size">{formatFileSize(doc.size)}</span>
                        )}
                        {doc.uploadDate && (
                          <span className="upload-date">{formatDate(doc.uploadDate)}</span>
                        )}
                        {doc.processingStatus && (
                          <span className={`status-badge ${doc.processingStatus}`}>
                            {doc.processingStatus}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {filteredDocuments.length > 0 && (
            <div className="dropdown-footer">
              <div className="selection-summary">
                {selectedDocuments.length > 0 ? 
                  `${getDocumentName(selectedDocuments[0])} selected` : 
                  'No document selected'
                }
              </div>
              <button
                type="button"
                className="done-button"
                onClick={() => setIsOpen(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}